import { Component, Input, OnInit } from '@angular/core';
import { AbstractBreezeListComponent } from '@common/classes/breeze-list';
import { EntityFormOptions } from '@common/classes/entity-form';
import { ExportType } from '@common/classes/export-type';
import { VesselNotificationPermissions } from '@common/classes/permissions';
import { AppControlType } from '@common/components/app-control/app-control.component';
import { User } from '@common/models/User';
import { BreezeViewService } from '@common/services/breeze-view.service';
import { PrintService } from '@common/services/print.service';

@Component({
    selector: 'app-vessel-notification-passenger',
    templateUrl: './vessel-notification-passenger.component.html',
    styleUrls: ['./vessel-notification-passenger.component.scss']
})
export class VesselNotificationPassengerComponent extends AbstractBreezeListComponent implements OnInit {
    queryName = 'VesselNotificationPassengers';
    exportCommand = 'ExportVesselNotificationPaxList';
    persistFilter = false;
    passengerOptions = this.getPassengerOptions();

    @Input() model: any;
    @Input() canEdit: boolean;
    @Input() editMode: any;
    @Input() shortNotification = false;
    @Input() user: User;

    passengerVehicleOptions: EntityFormOptions;

    constructor(breezeViewService: BreezeViewService,
        private printService: PrintService) { super(breezeViewService); }

    ngOnInit(): void {
        this.passengerVehicleOptions = this.getPassengerVehicleOptions();
        super.ngOnInit();
    }

    getPassengerOptions(): EntityFormOptions {
        return {
            entityName: 'VesselNotificationPassenger',
            canEdit: () => this.canEdit,
            beforeAdd: (data) => data.sequenceNumber = this.model.passengers.length + 1,
            onAdd: (entity) => {
                // Entity form adds the item to a filtered array, we have to manually add the entity to the real array
                this.model.passengers.push(entity);
                this.calculatePassengers();
                this.model.entityAspect.validateEntity();
                this.passengerVehicleOptions.propertyGroups[0].find(p => p.name === 'passengerFullName').options = this.setPassengerSelection();
            },
            onRemove: () => {
                this.calculatePassengers();
                this.model.entityAspect.validateEntity();
                this.passengerVehicleOptions.propertyGroups[0].find(p => p.name === 'passengerFullName').options = this.setPassengerSelection();
            },
            propertyGroups: [
                [
                    { name: 'familyName', label: 'Family Name', maxlength: 100, pattern: '[^0-9]' },
                    { name: 'givenName', label: 'Given Name', maxlength: 100, pattern: '[^0-9]' },
                    { name: 'nationalityId', label: 'Nationality', type: AppControlType.CodeList, codelist: 'CountryNationality' },
                    { name: 'birthDate', label: 'Date of Birth', type: AppControlType.DateTime }
                ],
                [
                    { name: 'birthPlace', label: 'Place of Birth', maxlength: 100 },
                    { name: 'genderId', label: 'Gender', type: AppControlType.CodeList, codelist: 'Gender' },
                    { name: 'personCategoryId', label: 'Person Category', type: AppControlType.CodeList, codelist: 'PersonCategory' },
                    { name: 'health', label: 'Health', type: AppControlType.Boolean }
                ],
                [
                    { name: 'documentTypeId', label: 'Nature of ID', type: AppControlType.CodeList, codelist: 'DocumentType' },
                    { name: 'documentNumber', label: 'ID Number', maxlength: 20 },
                    { name: 'documentIssuingCountryId', label: 'ID Issuing Country', type: AppControlType.CodeList, codelist: 'Country' },
                    { name: 'documentExpirationDate', label: 'ID Expiration Date', type: AppControlType.DateTime }
                ],
                [
                    { name: 'embarkationPortId', label: 'Port of Embarkation', type: AppControlType.CodeList, codelist: 'Location' },
                    { name: 'disembarkationPortId', label: 'Port of Disembarkation', type: AppControlType.CodeList, codelist: 'Location' },
                    { name: 'specialCareOrAssistanceInformation', label: 'Special care or assistance information', maxlength: 250, colSize: 3 },
                    { name: 'contactNumber', label: 'Contact Number', pattern: '[\\d]', prepend: '+', maxlength: 20, colSize: 3 }
                ],
                [
                    { name: 'permitNumber', label: 'Visa Number', maxlength: 20, colSize: 3 },
                    { name: 'transit', label: 'Transit', type: AppControlType.Boolean }
                ]
            ]
        };
    }

    getPassengerVehicleOptions(): EntityFormOptions {
        return {
            entityName: 'VesselNotificationPassengerVehicle',
            canEdit: () => this.canEdit,
            beforeAdd: (model) => {
                const pax = this.model.passengers.find(x => model.passengerFullName === `${x.givenName} ${x.familyName}`);
                if (!pax) throw new Error(`Passenger with full name ${model.passengerFullName} not found`);
                model.vesselNotificationPassengerId = pax.id;
            },
            propertyGroups: [
                [
                    {
                        name: 'passengerFullName',
                        label: 'Passenger full name',
                        type: AppControlType.Select,
                        options: this.setPassengerSelection()
                    },
                    {
                        name: 'vehicleTypeId',
                        label: 'Vehicle Type',
                        type: AppControlType.CodeList,
                        codelist: 'VehicleType'
                    },
                    {
                        name: 'vehicleModel',
                        label: 'Vehicle Model'
                    },
                    {
                        name: 'vehiclePlate',
                        label: 'Plate No.'
                    }
                ]
            ]
        };
    }

    getDefaultFilter() {
        return {
            id: this.model?.id
        };
    }

    calculatePassengers() {
        this.model.numberOfPax = this.model.passengers?.length;
        this.model.personsOnBoard = this.model.numberOfPax + this.model.workers?.length + (this.model.numberOfStowaways || 0);
    }

    setPassengerSelection() {
        return this.model.passengers.map((x: any) => ({ label: `${x.givenName} ${x.familyName}`, value: `${x.givenName} ${x.familyName}` }));
    }

    override export(exportType: ExportType) {
        if (exportType === ExportType.Pdf) this.printService.printDocument('/vessel-notification', [this.model.id]);
        else return super.export(exportType, this.appGrid);
    }

    canExport() {
        return this.user?.hasPermission(VesselNotificationPermissions.Action.exportPaxList);
    }

    get canSeeFilterAndExport() {
        return !this.editMode && ['D', 'A', 'W', 'R', 'AR', 'DP'].includes(this.model.statusId);
    }
}
